/**
 * หน้าจอ Executive Dashboard (P2-10d) — the projection ที่ `WO-P2-M10-002/003`
 * fold ไว้, served ครั้งแรกโดย read API ของ `WO-P2-M10-004`, และตอนนี้มีคนดูมันได้
 *
 * **หน้าจอนี้ไม่เหมือนหน้าจออื่นทุกตัวในระบบนี้ตรงที่มันตั้งใจไม่จำกัดขอบเขตโรงงาน
 * ของผู้ดู** (D-106 ข้อ 4, `docs/SECURITY.md` — ข้อยกเว้นข้อแรกของ D-004 ที่ตั้งใจ)
 * ผู้ถือสิทธิ์ `EXECUTIVE_DASHBOARD` + VIEW เห็นทุกโรงงานเสมอ ไม่ว่า `plant_scope`
 * ของตัวเองจะเป็นอะไร — ตัวสลับ `scope` ที่ผู้ใช้ขอไว้คือสิ่งที่ทำให้ข้อยกเว้นนี้
 * มองเห็นได้แทนที่จะเป็นเรื่องน่าประหลาดใจ:
 *
 * 1. **ตัวสลับคือหัวใจของหน้าจอนี้** — `scope=PLANT` คือแถวแยกรายโรงงาน และ
 *    `scope=CONSOLIDATED` คือยอดรวมทั้งบริษัทที่ fold ไว้ตั้งแต่ write time สองคำตอบ
 *    นี้เป็นคนละเลขกันแม้จะมีรูปเดียวกัน — จึงต้องเห็นได้ทันทีว่ากำลังดูฝั่งไหนอยู่
 *    ผ่านข้อความบนตัวหน้าจอเอง (`executive-scope-indicator`) ไม่ใช่แค่ค่าที่ตัว
 *    ควบคุมถืออยู่ เพราะคนที่มองข้ามตัวเลือกจะยกเลขของโรงงานเดียวไปพูดแทนทั้งบริษัท
 * 2. **(i) ข้างหัวเรื่องบอกข้อยกเว้นนี้ตรง ๆ** — คนที่ถูกบอกมาทั้งสัปดาห์ว่า "เห็นแค่
 *    โรงงานของตัวเอง" ต้องถูกบอกครั้งหนึ่งว่าที่นี่ไม่ใช่
 * 3. **หน่วยอ่านจากแถวที่เซิร์ฟเวอร์ตอบมา ไม่ map จาก metric code** —
 *    `COUNT` แสดงเป็นจำนวนเต็ม ส่วน `AMOUNT`/`QTY` คงสเกล `NUMERIC(18, 4)` ไว้เต็ม
 *    (`lib/executiveSummary/summaryView.ts`)
 * 4. **ช่วงว่างคือหน้าว่าง ไม่ใช่ error** และไม่แสดงตัวเลขของช่วงก่อนหน้าค้างไว้ —
 *    เปลี่ยนช่วง/scope แล้วต้องเห็นสถานะ `loading` ก่อนเห็นข้อมูลใหม่เสมอ
 * 5. **อ่านอย่างเดียวทั้งหน้า** — ไม่มีปุ่มแก้ไข ไม่มีปุ่ม recompute เพราะตารางนี้ไม่มี
 *    เส้นทางเขียนเลย (D-106 ข้อ 5) `service.rebuild()` มีอยู่จริงแต่ตั้งใจไม่ใช่ route
 *
 * ⚠️ ทั้งหมดนี้เป็น UX layer ไม่ใช่ด่านความปลอดภัย เซิร์ฟเวอร์เป็นผู้ตัดสินทุก
 * request เสมอ (`references/authorization-rbac.md` ข้อ 5) — และ `DesktopModulePage`
 * ปฏิเสธผู้ที่ไม่ถือ `EXECUTIVE_DASHBOARD` + VIEW ไปแล้วก่อนหน้าจอนี้จะเรนเดอร์
 */

import { useEffect, useState } from 'react';

import { StatusBadge } from '../StatusBadge';
import { InfoHint } from '../components/InfoHint';
import {
  fetchExecutiveSummary,
  ruleFromFailure,
  type ExecutiveSummaryFailure,
} from '../../lib/executiveSummary/client';
import {
  defaultDateRange,
  formatMetricValue,
  isRequestableRange,
  isReversedRange,
  maxDateToFor,
  metricUnitLabel,
  rangeExceedsMaxDays,
  scopeHint,
  scopeLabel,
} from '../../lib/executiveSummary/summaryView';
import {
  EXECUTIVE_SUMMARY_MAX_RANGE_DAYS,
  SNAPSHOT_SCOPES,
  type ExecutiveSummaryItem,
  type SnapshotScope,
} from '../../lib/executiveSummary/types';

export interface ExecutiveDashboardScreenProps {
  readonly apiBaseUrl: string;
  /** inject ได้เพื่อการทดสอบ — production ใช้ `fetch` ของเบราว์เซอร์ */
  readonly fetchImpl?: typeof fetch;
}

type LoadState =
  | { readonly status: 'loading' }
  | {
      readonly status: 'ready';
      readonly items: readonly ExecutiveSummaryItem[];
      readonly nextCursor: string | null;
      readonly loadingMore: boolean;
      readonly loadMoreFailure: ExecutiveSummaryFailure | null;
    }
  | ExecutiveSummaryFailure;

/** ข้อความของแต่ละความล้มเหลว — อ่านอย่างเดียวเสมอ จึงไม่มี "ไม่มีอะไรเปลี่ยน" ให้พูดถึง */
function failureCopy(failure: ExecutiveSummaryFailure): { heading: string; text: string } {
  switch (failure.status) {
    case 'unauthenticated':
      return {
        heading: 'ต้องเข้าสู่ระบบก่อนจึงจะใช้หน้าจอนี้ได้',
        text: 'เซิร์ฟเวอร์ตอบ 401 — เซสชันหมดอายุหรือถูกยกเลิกไปแล้ว ต้องเข้าสู่ระบบใหม่ (D-049)',
      };
    case 'forbidden':
      return {
        heading: 'สิทธิ์ชุดนี้ไม่ครอบคลุมคำขอนี้',
        text: 'เซิร์ฟเวอร์ตอบ 403 — คำขอนี้ต้องการโมดูล EXECUTIVE_DASHBOARD ระดับ VIEW ขอสิทธิ์เพิ่มที่ตารางสิทธิ์ตามแผนก',
      };
    case 'rule-violation': {
      const rule = ruleFromFailure(failure);
      return {
        heading: 'ช่วงวันที่หรือตัวกรองที่ส่งไปติดกฎของเส้นนี้',
        text: `เซิร์ฟเวอร์ตอบ 422${rule === null ? '' : ` พร้อมกฎ ${rule}`} — ตรวจช่วงวันที่และลองใหม่`,
      };
    }
    case 'bad-request':
      return {
        heading: 'คำขอนี้ถูกปฏิเสธว่าไม่ถูกต้อง',
        text: 'เซิร์ฟเวอร์ตอบ 400 — พารามิเตอร์ที่ส่งไปไม่ตรงรูปแบบที่ API รับ',
      };
    case 'server-error':
      return {
        heading: `เซิร์ฟเวอร์ตอบ ${failure.httpStatus}`,
        text: 'คำขอไปถึงเซิร์ฟเวอร์แล้วแต่ทำงานไม่สำเร็จ ยังไม่มีข้อมูลจริงมาแสดง',
      };
    case 'network-error':
      return {
        heading: 'ติดต่อเซิร์ฟเวอร์ไม่ได้',
        text: `${failure.detail} — หน้าจอนี้จึงไม่มีข้อมูลจะแสดง และจะไม่เดาขึ้นมาเอง`,
      };
    case 'invalid-response':
      return {
        heading: 'ข้อมูลจากเซิร์ฟเวอร์ผิดรูปแบบ',
        text: 'หน้าจอปฏิเสธที่จะแสดงตัวเลขที่อ่านได้ไม่ครบ — ตัวเลขที่หายไปหนึ่งแถวคือความเสียหายที่ไม่มีใครมองเห็น',
      };
    case 'cancelled':
      return { heading: 'ยกเลิกการเรียกข้อมูลแล้ว', text: 'คำขอถูกยกเลิกก่อนได้รับคำตอบ' };
  }
}

function ExecutiveSummaryFailureNotice({
  failure,
  testIdSuffix,
  onRetry,
}: {
  readonly failure: ExecutiveSummaryFailure;
  readonly testIdSuffix: string;
  readonly onRetry?: () => void;
}) {
  const copy = failureCopy(failure);
  return (
    <div
      className="cap-notice"
      data-testid={`executive-failure${testIdSuffix}`}
      data-status={failure.status}
      role="status"
    >
      <StatusBadge tone="danger">ทำรายการไม่สำเร็จ</StatusBadge>
      <h3 className="cap-notice__title">{copy.heading}</h3>
      <p className="cap-notice__text">{copy.text}</p>
      {onRetry === undefined ? null : (
        <div className="cap-notice__actions">
          <button type="button" className="cap-button cap-button--ghost" onClick={onRetry}>
            ลองอีกครั้ง
          </button>
        </div>
      )}
    </div>
  );
}

/** แถวหนึ่งแสดงจำนวนโรงงาน — "—" สำหรับแถวรวมทั้งบริษัท (`plant_id === null`) */
function plantCell(item: ExecutiveSummaryItem): string {
  return item.plant_id ?? '—';
}

export function ExecutiveDashboardScreen({
  apiBaseUrl,
  fetchImpl,
}: ExecutiveDashboardScreenProps) {
  const [scope, setScope] = useState<SnapshotScope>('PLANT');
  const initialRange = defaultDateRange();
  const [dateFrom, setDateFrom] = useState(initialRange.date_from);
  const [dateTo, setDateTo] = useState(initialRange.date_to);
  const [state, setState] = useState<LoadState>({ status: 'loading' });
  const [reloadToken, setReloadToken] = useState(0);

  const reversed = isReversedRange(dateFrom, dateTo);
  const tooWide = rangeExceedsMaxDays(dateFrom, dateTo);
  const requestable = isRequestableRange(dateFrom, dateTo);

  useEffect(() => {
    if (!requestable) {
      // ช่วงกลับด้าน/เกินขอบเขตถูกกันไว้ที่ฟอร์ม — ไม่ส่งคำขอที่รู้อยู่แล้วว่าจะโดน
      // 422 และ **ไม่แสดงตัวเลขของช่วงก่อนหน้าค้างไว้** เปลี่ยนกลับเป็นสถานะ loading
      setState({ status: 'loading' });
      return;
    }

    const controller = new AbortController();
    let active = true;
    setState({ status: 'loading' });

    void fetchExecutiveSummary(
      { scope, date_from: dateFrom, date_to: dateTo },
      { apiBaseUrl, fetchImpl, signal: controller.signal },
    ).then((result) => {
      if (!active || result.status === 'cancelled') {
        return;
      }
      setState(
        result.status === 'ready'
          ? {
              status: 'ready',
              items: result.page.items,
              nextCursor: result.page.next_cursor,
              loadingMore: false,
              loadMoreFailure: null,
            }
          : result,
      );
    });

    return () => {
      active = false;
      controller.abort();
    };
  }, [apiBaseUrl, fetchImpl, scope, dateFrom, dateTo, requestable, reloadToken]);

  const loadMore = async () => {
    if (state.status !== 'ready' || state.loadingMore) {
      return;
    }
    const loaded = state;
    const cursor = loaded.nextCursor;
    if (cursor === null) {
      return;
    }
    setState({ ...loaded, loadingMore: true, loadMoreFailure: null });

    const result = await fetchExecutiveSummary(
      { scope, date_from: dateFrom, date_to: dateTo, cursor },
      { apiBaseUrl, fetchImpl },
    );

    if (result.status === 'cancelled') {
      setState({ ...loaded, loadingMore: false });
      return;
    }
    setState(
      result.status === 'ready'
        ? {
            status: 'ready',
            items: [...loaded.items, ...result.page.items],
            nextCursor: result.page.next_cursor,
            loadingMore: false,
            loadMoreFailure: null,
          }
        : { ...loaded, loadingMore: false, loadMoreFailure: result },
    );
  };

  const maxDateTo = maxDateToFor(dateFrom);

  return (
    <div className="cap-page" data-testid="executive-dashboard">
      <div className="cap-page__head">
        <div>
          <h1 className="cap-page__title">
            ภาพรวมผู้บริหาร (Executive Dashboard)
            <InfoHint
              label="เกี่ยวกับขอบเขตโรงงานที่แสดงในหน้าจอนี้"
              text="หน้าจอนี้แสดงตัวเลขของทุกโรงงาน ไม่จำกัดตามขอบเขตโรงงานของคุณเหมือนหน้าจออื่นในระบบนี้ (D-106) เพราะเป็นสิทธิ์เดียวที่ตั้งใจให้ข้ามขอบเขตโรงงาน"
            />
            <InfoHint
              label="เกี่ยวกับที่มาของตัวเลขในหน้าจอนี้"
              text="ตัวเลขที่ fold ไว้แล้วจากเหตุการณ์ทางธุรกิจ — อ่านอย่างเดียว ไม่มีปุ่มแก้ไข หรือคำนวณใหม่ ตัวเลขที่ดูผิดต้องแก้ที่ต้นทาง (เหตุการณ์หรือการ fold) แล้ว replay การ fold ใหม่เท่านั้น"
            />
          </h1>
        </div>
      </div>

      <div className="cap-filters" data-testid="executive-toolbar">
        <label className="cap-field">
          <span className="cap-field__label">มุมมอง (Scope)</span>
          <select
            className="cap-input"
            data-testid="executive-scope-select"
            value={scope}
            onChange={(event) => {
              setScope(event.target.value as SnapshotScope);
            }}
          >
            {SNAPSHOT_SCOPES.map((value) => (
              <option key={value} value={value}>
                {scopeLabel(value)}
              </option>
            ))}
          </select>
          <InfoHint label="เกี่ยวกับมุมมองที่เลือกอยู่" text={scopeHint(scope)} />
        </label>

        <label className="cap-field">
          <span className="cap-field__label">จากวันที่</span>
          <input
            className="cap-input"
            type="date"
            data-testid="executive-date-from"
            value={dateFrom}
            max={dateTo}
            onChange={(event) => {
              setDateFrom(event.target.value);
            }}
          />
        </label>

        <label className="cap-field">
          <span className="cap-field__label">ถึงวันที่</span>
          <input
            className="cap-input"
            type="date"
            data-testid="executive-date-to"
            value={dateTo}
            min={dateFrom}
            max={maxDateTo}
            onChange={(event) => {
              setDateTo(event.target.value);
            }}
          />
        </label>
      </div>

      {/*
        ตัวสลับ scope คือหัวใจของหน้าจอนี้ (invariant: "the current scope must be
        visible in the page itself, not only in the control that set it") — ข้อความ
        นี้จึงอยู่นอกตัวควบคุม ไม่ใช่แค่ label ของมัน
      */}
      <p className="cap-page__sub" data-testid="executive-scope-indicator">
        กำลังแสดง: <strong>{scopeLabel(scope)}</strong>
      </p>

      {reversed ? (
        <div className="cap-notice" data-testid="executive-range-reversed" role="status">
          <StatusBadge tone="warning">ช่วงวันที่ไม่ถูกต้อง</StatusBadge>
          <p className="cap-notice__text">
            วันที่เริ่มต้นอยู่หลังวันที่สิ้นสุด — แก้ช่วงวันที่ก่อนจึงจะเห็นข้อมูล
          </p>
        </div>
      ) : tooWide ? (
        <div className="cap-notice" data-testid="executive-range-too-wide" role="status">
          <StatusBadge tone="warning">ช่วงวันที่กว้างเกินไป</StatusBadge>
          <p className="cap-notice__text">
            เส้นนี้ตอบได้ไม่เกิน {EXECUTIVE_SUMMARY_MAX_RANGE_DAYS} วันต่อคำขอ — แคบช่วง
            วันที่ลงก่อนจึงจะเห็นข้อมูล
          </p>
        </div>
      ) : state.status === 'loading' ? (
        <p className="cap-page__sub" data-testid="executive-loading">
          กำลังโหลด…
        </p>
      ) : state.status === 'ready' ? (
        state.items.length === 0 ? (
          <div className="cap-notice" data-testid="executive-empty" role="status">
            <StatusBadge tone="info">ไม่มีข้อมูล</StatusBadge>
            <p className="cap-notice__text">
              ไม่มีข้อมูลสำหรับช่วงวันที่นี้ — ไม่มีเหตุการณ์ใดถูก fold ไว้ในช่วงนี้
              ซึ่งเป็นคำตอบปกติของวันที่เงียบ ไม่ใช่ความผิดพลาด
            </p>
          </div>
        ) : (
          <>
            <div className="cap-table-wrap">
              <table className="cap-table" data-testid="executive-summary-table">
                <caption className="cap-visually-hidden">
                  ตัวเลขที่ fold แล้ว ตาม scope และช่วงวันที่ที่เลือก
                </caption>
                <thead>
                  <tr>
                    <th scope="col">วันที่</th>
                    <th scope="col">รหัสตัวชี้วัด</th>
                    <th scope="col">โรงงาน</th>
                    <th scope="col">หน่วย</th>
                    <th scope="col">ค่า</th>
                  </tr>
                </thead>
                <tbody>
                  {state.items.map((item, index) => (
                    <tr
                      key={`${item.period_start}-${item.metric_code}-${item.plant_id ?? 'ALL'}-${index}`}
                      data-testid={`executive-row-${index}`}
                    >
                      <td>
                        <code className="cap-table__code">{item.period_start}</code>
                      </td>
                      <td>
                        <code className="cap-table__code">{item.metric_code}</code>
                      </td>
                      <td>
                        {item.plant_id === null ? (
                          <span className="cap-table__muted">— (ทั้งบริษัท)</span>
                        ) : (
                          <code className="cap-table__code">{plantCell(item)}</code>
                        )}
                      </td>
                      <td>{metricUnitLabel(item.metric_unit)}</td>
                      <td data-testid={`executive-value-${index}`}>
                        {formatMetricValue(item.metric_value, item.metric_unit)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {state.nextCursor !== null ? (
              <div className="cap-page__actions">
                <button
                  type="button"
                  className="cap-button cap-button--ghost"
                  data-testid="executive-load-more"
                  onClick={() => {
                    void loadMore();
                  }}
                  disabled={state.loadingMore}
                >
                  {state.loadingMore ? 'กำลังโหลดเพิ่ม…' : 'โหลดรายการถัดไป'}
                </button>
              </div>
            ) : null}

            {state.loadMoreFailure !== null ? (
              <ExecutiveSummaryFailureNotice
                failure={state.loadMoreFailure}
                testIdSuffix="-load-more"
              />
            ) : null}
          </>
        )
      ) : (
        <ExecutiveSummaryFailureNotice
          failure={state}
          testIdSuffix=""
          onRetry={() => {
            setReloadToken((token) => token + 1);
          }}
        />
      )}
    </div>
  );
}
